import { memo, useCallback, useState } from 'react';

import { cn } from '@air/utils-tailwind';

import { WorkspaceAgentGuidedVariantCropOriginField } from '~/components/v2/components/workspace/workspace-agent-panel/components/composer/guided-variants/WorkspaceAgentGuidedVariantCropOriginField';
import { WorkspaceAgentGuidedVariantScaleOrCropField } from '~/components/v2/components/workspace/workspace-agent-panel/components/composer/guided-variants/WorkspaceAgentGuidedVariantScaleOrCropField';
import { WorkspaceAgentGuidedVariantSettingsActions } from '~/components/v2/components/workspace/workspace-agent-panel/components/composer/guided-variants/WorkspaceAgentGuidedVariantSettingsActions';
import { WorkspaceAgentGuidedVariantSizeSavePopover } from '~/components/v2/components/workspace/workspace-agent-panel/components/composer/guided-variants/WorkspaceAgentGuidedVariantSizeSavePopover';
import { type WorkspaceAgentGuidedVariantGenerateRequest } from '~/components/v2/components/workspace/workspace-agent-panel/components/composer/guided-variants/workspaceAgentGuidedVariantGenerateRequest';
import {
  buildWorkspaceAgentScaleOrCropPrompt,
  isWorkspaceAgentScaleOrCropCropSize,
  WORKSPACE_AGENT_DEFAULT_SCALE_OR_CROP_ORIGIN,
  type WorkspaceAgentScaleOrCropOrigin,
  type WorkspaceAgentScaleOrCropSize,
  workspaceAgentScaleOrCropSizeLabel,
  workspaceAgentSmartResizeSizesFromScaleOrCrop,
} from '~/components/v2/components/workspace/workspace-agent-panel/utils/workspaceAgentScaleOrCrop';
import {
  selectionIsSavedSize,
  selectionMatchesSavedSet,
  WORKSPACE_AGENT_SMART_RESIZE_MAX_SIZES,
} from '~/components/v2/components/workspace/workspace-agent-panel/utils/workspaceAgentSmartResize';
import {
  canSaveWorkspaceAgentSmartResizeSelection,
  type WorkspaceAgentSmartResizeSavedCatalog,
} from '~/components/v2/components/workspace/workspace-agent-panel/utils/workspaceAgentSmartResizeSavedCatalog';

export type WorkspaceAgentGuidedVariantScaleOrCropSettingsProps = {
  imageCount: number;
  onGenerate: (request: WorkspaceAgentGuidedVariantGenerateRequest) => void;
  savedCatalog: WorkspaceAgentSmartResizeSavedCatalog;
  skippedFileCount: number;
};

export const WorkspaceAgentGuidedVariantScaleOrCropSettings = memo(
  ({ imageCount, onGenerate, savedCatalog, skippedFileCount }: WorkspaceAgentGuidedVariantScaleOrCropSettingsProps) => {
    const [sizes, setSizes] = useState<WorkspaceAgentScaleOrCropSize[]>([]);
    const [origin, setOrigin] = useState<WorkspaceAgentScaleOrCropOrigin>(WORKSPACE_AGENT_DEFAULT_SCALE_OR_CROP_ORIGIN);
    const { isLoading: isSavedCatalogLoading, onSave, sets: savedSets, sizes: savedSizes } = savedCatalog;
    const outputCount = imageCount * sizes.length;
    const isGenerateDisabled =
      imageCount < 1 || sizes.length < 1 || sizes.length > WORKSPACE_AGENT_SMART_RESIZE_MAX_SIZES;
    const generateLabel = isGenerateDisabled ? 'Generate' : `Generate ${outputCount.toLocaleString()}`;
    const previewCropSize = sizes.findLast(isWorkspaceAgentScaleOrCropCropSize);
    const savableSizes = workspaceAgentSmartResizeSizesFromScaleOrCrop(sizes).filter((size) =>
      canSaveWorkspaceAgentSmartResizeSelection([size]),
    );
    const showSave =
      savableSizes.length > 0 &&
      !selectionIsSavedSize(savableSizes, savedSizes) &&
      !selectionMatchesSavedSet(savableSizes, savedSets) &&
      !isSavedCatalogLoading &&
      onSave !== undefined;

    const handleGenerate = useCallback(() => {
      if (imageCount < 1 || sizes.length < 1 || sizes.length > WORKSPACE_AGENT_SMART_RESIZE_MAX_SIZES) {
        return;
      }

      onGenerate({
        allowsJpeg: true,
        kind: 'content',
        build: (destination, output) =>
          buildWorkspaceAgentScaleOrCropPrompt({
            destination,
            imageCount,
            origin,
            output,
            sizes,
            skippedFileCount,
          }),
        structuredDisplay: {
          description: sizes.map((size) => workspaceAgentScaleOrCropSizeLabel(size)).join(', '),
          icon: 'scaling',
          title: 'Scale or crop',
        },
      });
    }, [imageCount, onGenerate, origin, sizes, skippedFileCount]);

    const handleSaveName = useCallback(
      async (name: string): Promise<boolean> => {
        if (savableSizes.length < 1 || !onSave) {
          return false;
        }

        return onSave({ name, sizes: savableSizes });
      },
      [onSave, savableSizes],
    );

    return (
      <div className="flex flex-col gap-3" data-testid="WORKSPACE_AGENT_GUIDED_VARIANT_SCALE_OR_CROP_SETTINGS">
        <WorkspaceAgentGuidedVariantScaleOrCropField
          isSavedCatalogLoading={isSavedCatalogLoading}
          onSizesChange={setSizes}
          savedSets={savedSets}
          savedSizes={savedSizes}
          sizes={sizes}
        />
        {previewCropSize ? (
          <WorkspaceAgentGuidedVariantCropOriginField
            cropSize={previewCropSize}
            onOriginChange={setOrigin}
            origin={origin}
          />
        ) : null}
        <div className={cn('flex items-center gap-2', showSave ? 'justify-between' : 'justify-end')}>
          {showSave ? (
            <WorkspaceAgentGuidedVariantSizeSavePopover onSave={handleSaveName} sizeCount={savableSizes.length} />
          ) : null}
          <WorkspaceAgentGuidedVariantSettingsActions
            generateLabel={generateLabel}
            isGenerateDisabled={isGenerateDisabled}
            onGenerate={handleGenerate}
          />
        </div>
      </div>
    );
  },
);

WorkspaceAgentGuidedVariantScaleOrCropSettings.displayName = 'WorkspaceAgentGuidedVariantScaleOrCropSettings';
